import { Link } from "react-router-dom";
import { useTranslation } from "../i18n";
import Wordmark from "../routes/newApp/assets/juhuu-wordmark.png";
import AppStoreBadge from "../routes/newApp/assets/app-store-badge.webp";
import GooglePlayBadge from "../routes/newApp/assets/google-play-badge.webp";

const STORE = {
  ios: "https://apps.apple.com/at/app/juhuu/id1603108125",
  android: "https://play.google.com/store/apps/details?id=com.juhuu_bikebox.juhuu",
};

const LEGAL_LINKS = [
  { href: "https://docs.juhuu.app/legal/imprint", key: "imprint" },
  { href: "https://docs.juhuu.app/legal/privacy", key: "privacy" },
  { href: "https://docs.juhuu.app/legal/cookies", key: "cookies" },
  {
    href: "https://docs.juhuu.app/legal/global-terms-of-service-agreement",
    key: "termsOfService",
  },
];

export default function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  const products = [
    { href: "/app", label: t("components.nav.products.app") },
    { href: "/dashboard", label: t("components.nav.products.dashboard") },
    { href: "/locking-systems", label: t("components.nav.products.locking") },
  ];

  const industries = [
    { href: "/industries#parking", label: t("components.nav.industries.bikeParking") },
    { href: "/industries#rental", label: t("components.nav.industries.bikeRental") },
    { href: "/industries#custom", label: t("components.nav.industries.other") },
  ];

  const colLink =
    "block text-[.94rem] text-muted transition-colors hover:text-juhuu mb-2.5";
  const colHeading =
    "mb-4 text-[.76rem] font-bold uppercase tracking-[.12em] text-muted";

  return (
    <footer className="border-t border-line bg-[#faf9fd] px-4 pb-10 pt-16 md:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-[1.5fr_repeat(4,1fr)]">
          {/* Brand column */}
          <div className="max-w-[300px]">
            <img src={Wordmark} alt="JUHUU" className="mb-4 h-6 w-auto" />
            <p className="text-[.9rem] leading-[1.6] text-muted">
              {t("components.siteFooter.tagline")}
            </p>

            <div className="mt-5">
              <span className="text-[.72rem] font-bold uppercase tracking-[.1em] text-muted">
                {t("components.nav.getTheApp")}
              </span>
              <div className="mt-2.5 flex flex-wrap gap-2">
                <a href={STORE.ios} target="_blank" rel="noopener noreferrer">
                  <img
                    src={AppStoreBadge}
                    alt={t("routes.app.appStoreAlt")}
                    className="h-10 w-[138px] object-contain"
                  />
                </a>
                <a href={STORE.android} target="_blank" rel="noopener noreferrer">
                  <img
                    src={GooglePlayBadge}
                    alt={t("routes.app.googlePlayAlt")}
                    className="h-10 w-[138px] object-contain"
                  />
                </a>
              </div>
            </div>

            <div className="mt-5">
              <a
                href="https://www.linkedin.com/company/juhuu/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="JUHUU on LinkedIn"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line bg-white text-muted transition-colors hover:border-juhuu hover:text-juhuu"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4.98 3.5A2.5 2.5 0 1 1 0 3.5a2.5 2.5 0 0 1 4.98 0zM.25 8.25h4.5V24h-4.5V8.25zM8.5 8.25h4.3v2.15h.06c.6-1.13 2.06-2.32 4.24-2.32 4.54 0 5.38 2.99 5.38 6.87V24h-4.5v-6.98c0-1.66-.03-3.8-2.32-3.8-2.32 0-2.68 1.81-2.68 3.68V24h-4.5V8.25z" />
                </svg>
              </a>
            </div>
          </div>

          <div>
            <h4 className={colHeading}>{t("components.nav.productLabel")}</h4>
            {products.map((p) => (
              <Link key={p.href} to={p.href} className={colLink}>
                {p.label}
              </Link>
            ))}
          </div>

          <div>
            <h4 className={colHeading}>{t("components.nav.industriesLabel")}</h4>
            {industries.map((b) => (
              <Link key={b.href} to={b.href} className={colLink}>
                {b.label}
              </Link>
            ))}
          </div>

          <div>
            <h4 className={colHeading}>{t("components.nav.companyLabel")}</h4>
            <Link to="/references" className={colLink}>
              {t("components.nav.references")}
            </Link>
            <Link to="/team" className={colLink}>
              {t("components.nav.about")}
            </Link>
            <Link to="/meeting" className={colLink}>
              {t("components.nav.bookDemo")}
            </Link>
          </div>

          <div>
            <h4 className={colHeading}>{t("components.siteFooter.legalLabel")}</h4>
            {LEGAL_LINKS.map((l) => (
              <a
                key={l.key}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className={colLink}
              >
                {t(`components.siteFooter.${l.key}`)}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-wrap justify-between gap-4 border-t border-line pt-6 text-[.82rem] text-muted">
          <span>
            © {year} JUHUU BikeBox GmbH.{" "}
            {t("components.siteFooter.rightsReserved")}
          </span>
          <div className="flex flex-wrap gap-5">
            <a
              href={LEGAL_LINKS[0].href}
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-juhuu"
            >
              {t("components.siteFooter.imprint")}
            </a>
            <a
              href={LEGAL_LINKS[1].href}
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-juhuu"
            >
              {t("components.siteFooter.privacy")}
            </a>
          </div>
        </div>

        <p className="mt-6 text-[.72rem] leading-[1.6] text-muted/70">
          © {year} Copyright: JUHUU Bikebox GmbH.{" "}
          {t("components.siteFooter.trademarks")}
        </p>
      </div>
    </footer>
  );
}
